import { Injectable } from '@angular/core';
import { AbstractSecurityStorage } from 'angular-auth-oidc-client';
import { environment } from '../../../environments/environment';

export const OIDC_CONFIG_ID = 'opogo-web';
export const OIDC_STORAGE_PREFIX = `opogo.oidc:${environment.oidcConfig.authority}:${environment.oidcConfig.clientId}:`;

interface TokenResult {
  access_token?: string;
  id_token?: string;
  refresh_token?: string;
  [key: string]: unknown;
}

export interface OidcTokens {
  authzData: string;
  authnResult: TokenResult;
  access_token_expires_at: number;
  userData: unknown;
}

export interface OidcSession {
  epoch: string;
  remember: boolean;
  tokens: OidcTokens;
}

@Injectable({ providedIn: 'root' })
export class OidcStorageService implements AbstractSecurityStorage {
  readonly sessionKey = OIDC_STORAGE_PREFIX + 'session';

  get hasSessionState(): boolean {
    return !!(localStorage.getItem(this.sessionKey) || sessionStorage.getItem(this.sessionKey) ||
      localStorage.getItem(OIDC_STORAGE_PREFIX + 'control'));
  }

  // The library mixes credentials with PKCE, nonce and state. Its working record
  // must stay tab-local; only a successfully validated token snapshot is shared.
  read(key: string): string | null {
    return sessionStorage.getItem(OIDC_STORAGE_PREFIX + 'library:' + key);
  }

  write(key: string, value: string): void {
    sessionStorage.setItem(OIDC_STORAGE_PREFIX + 'library:' + key, value);
  }

  remove(key: string): void {
    sessionStorage.removeItem(OIDC_STORAGE_PREFIX + 'library:' + key);
  }

  clear(): void {
    this.remove(OIDC_CONFIG_ID);
  }

  readSession(): OidcSession | null {
    const raw = localStorage.getItem(this.sessionKey) || sessionStorage.getItem(this.sessionKey);
    if (!raw) return null;
    try {
      const session: OidcSession = JSON.parse(raw);
      return session.tokens?.authzData && session.tokens?.authnResult?.id_token ? session : null;
    } catch {
      return null;
    }
  }

  hydrate(session: OidcSession): void {
    const current = JSON.parse(this.read(OIDC_CONFIG_ID) || '{}');
    // Refresh cannot reuse flags left by an interrupted document.
    delete current.storageSilentRenewRunning;
    delete current.storageCodeFlowInProgress;
    this.write(OIDC_CONFIG_ID, JSON.stringify({ ...current, ...session.tokens }));
  }

  capture(epoch: string, remember: boolean): OidcSession {
    const current = JSON.parse(this.read(OIDC_CONFIG_ID) || '{}');
    if (!current.authzData || !current.authnResult?.refresh_token) {
      throw new Error('The identity provider did not return a renewable session.');
    }
    const session: OidcSession = {
      epoch, remember,
      tokens: {
        authzData: current.authzData,
        authnResult: current.authnResult,
        access_token_expires_at: current.access_token_expires_at,
        userData: current.userData,
      },
    };
    (remember ? localStorage : sessionStorage).setItem(this.sessionKey, JSON.stringify(session));
    (remember ? sessionStorage : localStorage).removeItem(this.sessionKey);
    return session;
  }

  clearSession(shared: boolean): void {
    this.clear();
    sessionStorage.removeItem(this.sessionKey);
    if (shared) localStorage.removeItem(this.sessionKey);
  }
}
